/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app using OneLogin OIDC.
 * This wraps react-oidc-context to provide a simpler API and type-safe user object.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { oidcConfig } from './oidcConfig';

/**
 * User access roles from OneLogin
 * These must match the Roles enum in the shared data model (search_shared/models.py)
 */
export type UserAccessRole = 
  | 'employee'
  | 'customer'
  | 'partner'
  | 'trainer'
  | 'system'
  | 'admin'
  | 'anonymous';

/**
 * User information from OneLogin
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture?: string;
  /** User access roles from the params.user_access_role claim (supports single or multiple) */
  roles: UserAccessRole[];
}

/**
 * Authentication context state
 */
interface AuthContextType {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is being checked/loaded */
  isLoading: boolean;
  /** The authenticated user, or null if not authenticated */
  user: User | null;
  /** The raw access token (for API calls) */
  accessToken: string | null;
  /** Initiate login flow */
  login: () => void;
  /** Initiate logout flow */
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Inner provider that uses the OIDC hook
 */
function AuthContextProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth();

  // Debug: Log OIDC state changes
  // useEffect(() => {
  //   console.group('🔐 OIDC State (Debug)');
  //   console.log('isAuthenticated:', oidc.isAuthenticated);
  //   console.log('isLoading:', oidc.isLoading);
  //   console.log('error:', oidc.error);
  //   console.log('activeNavigator:', oidc.activeNavigator);
    
  //   if (oidc.user) {
  //     console.log('--- User Info ---');
  //     console.log('ID Token Claims:', oidc.user.profile);
  //     console.log('Access Token:', oidc.user.access_token?.substring(0, 50) + '...');
  //     console.log('Token Type:', oidc.user.token_type);
  //     console.log('Expires At:', oidc.user.expires_at ? new Date(oidc.user.expires_at * 1000).toISOString() : 'N/A');
  //     console.log('Scopes:', oidc.user.scope);
  //   } else {
  //     console.log('user: null');
  //   }
  //   console.groupEnd();
  // }, [oidc.isAuthenticated, oidc.isLoading, oidc.error, oidc.user, oidc.activeNavigator]);

  // Map OIDC user profile to our User type
  const user = useMemo<User | null>(() => {
    if (!oidc.user?.profile) return null;
    
    const profile = oidc.user.profile;
    
    // Extract custom claim for user access roles from the params claim
    // The params claim contains custom fields configured in OneLogin
    // user_access_role can be a single string or an array of strings
    const params = (profile as Record<string, unknown>)['params'] as Record<string, unknown> | undefined;
    const rawRoles = params?.['user_access_role'];
    
    // Normalize to array - handle both single string and array formats
    let roles: UserAccessRole[] = [];
    if (Array.isArray(rawRoles)) {
      roles = rawRoles as UserAccessRole[];
    } else if (typeof rawRoles === 'string') {
      // Roles are separated by | in the token
      roles = rawRoles.includes('|') 
        ? rawRoles.split('|').map(r => r.trim() as UserAccessRole)
        : [rawRoles as UserAccessRole];
    }

    
    return {
      id: profile.sub,
      email: profile.email ?? '',
      firstName: profile.given_name ?? profile.name?.split(' ')[0] ?? '',
      lastName: profile.family_name ?? profile.name?.split(' ').slice(1).join(' ') ?? '',
      picture: profile.picture,
      roles,
    };
  }, [oidc.user?.profile]);

  const value = useMemo<AuthContextType>(() => ({
    isAuthenticated: oidc.isAuthenticated,
    isLoading: oidc.isLoading,
    user,
    // NOTE: We deliberately send the ID token (not the access token) to the
    // backend. OneLogin only includes the custom `params` claim
    // (user_access_role, hubspot_id) in the ID token by default. The backend
    // validates the signature, issuer and audience either way.
    accessToken: oidc.user?.id_token ?? null,
    login: () =>
      oidc.signinRedirect({
        state: { returnTo: `${window.location.pathname}${window.location.search}` },
      }),
    logout: () => oidc.signoutRedirect(),
  }), [oidc.isAuthenticated, oidc.isLoading, user, oidc.user?.id_token, oidc]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider
 * 
 * Wraps the app with OIDC authentication context.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </OidcAuthProvider>
  );
}

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
